import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App.js";
import { Connect } from "./components/Connect.js";
import { AuthGate } from "./components/Login.js";
import { AdminSite } from "./components/AppAdmin.js";
import { needsHost, shell } from "./host.js";
import { useLocale } from "./i18n.js";
import { applyLook, readStyle, readTheme } from "./theme.js";
import "@fontsource-variable/manrope";
// The tactical style's faces. Declared here, fetched only once text on screen uses them.
import "@fontsource/barlow/400.css";
import "@fontsource/barlow/600.css";
import "@fontsource/barlow/700.css";
import "@fontsource/barlow-condensed/600.css";
import "@fontsource/barlow-condensed/700.css";
import "@fontsource-variable/jetbrains-mono";
// The industrial style's faces, fetched the same way. One weight of the wide face: its names and labels all sit at 600.
import "@fontsource-variable/ibm-plex-sans";
import "@fontsource/encode-sans-expanded/600.css";
// The vintage style's: a text serif, one weight of the display serif (names and headings all sit at 600), and a typewriter.
import "@fontsource-variable/source-serif-4";
import "@fontsource/playfair-display/600.css";
import "@fontsource/courier-prime/400.css";
import "@fontsource/courier-prime/700.css";
// Stellar's, terminal's (the pixel face has one weight, which is all it draws), studio's and glass's.
import "@fontsource-variable/cinzel";
import "@fontsource-variable/nunito-sans";
import "@fontsource/vt323";
import "@fontsource/ibm-plex-mono/400.css";
import "@fontsource/ibm-plex-mono/700.css";
import "@fontsource-variable/hanken-grotesk";
import "@fontsource-variable/outfit";
import "@fontsource-variable/dm-sans";
import "./styles/index.css";

const root = document.getElementById("root");
if (root == null) throw new Error("index.html has no #root element");
// Before the first paint, so a light page never flashes dark on its way in. The operators' site stays dark and calm.
applyLook(location.pathname === "/admin" ? { theme: "dark", style: "calm" } : { theme: readTheme(), style: readStyle() });
// The page is up: index.html's "did not start" message stands down, and stops listening for trouble.
const page = window as unknown as { __arkBootTimer?: number; __arkUp?: boolean };
clearTimeout(page.__arkBootTimer);
page.__arkUp = true;

// The worker that makes the app installable on a phone (client-app §7.3). Only
// a built app served over TLS or localhost gets one; the dev server does not,
// so a stale shell never shadows an edit, and the packaged app carries its own
// copy of the page, so it has nothing to keep.
if (import.meta.env.PROD && !shell() && !location.pathname.startsWith("/admin") && "serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").catch((error: unknown) => console.warn("service worker not registered:", error));
}
/**
 * The page. It reads the language (i18n.ts) so that a new choice draws the
 * whole tree again at once, every word in it, with nothing closed or reset.
 * The packaged app with no host yet has nothing to show but where to find one
 * (client-app §6).
 */
function Page(): React.JSX.Element {
  useLocale();
  return location.pathname === "/admin" ? <AdminSite /> : needsHost() ? <Connect current={null} /> : <AuthGate><App /></AuthGate>;
}

createRoot(root).render(
  <StrictMode>
    <Page />
  </StrictMode>,
);
